import { AsyncBoundary } from "@components/AsyncBoundary";
import { PanelError } from "@scribenote/brian-design-system";
import { type PaneSelection, resolveSelection } from "@subjects/paneSelection";
import { SubjectRail } from "@subjects/SubjectRail";
import { SubjectReader } from "@subjects/SubjectReader";
import type { SubjectKind } from "@subjects/subjectsClient";
import { useSubjectIndex } from "@subjects/useSubjectIndex";
import { useState } from "react";

/**
 * The subject pane's body (WEB-3288): the index rail, and the reader it drives.
 * Each region scrolls on its own. Selection is local state, not URL, and resets
 * via `SubjectPane`'s remount key.
 */
export const SubjectWorkspace = ({
  subjectId,
  kind,
  visitId,
  roomUuid,
}: SubjectWorkspaceProps) => (
  <AsyncBoundary
    skeleton={<WorkspaceSkeleton />}
    fallback={(retry) => <PanelError label="patient panel" onRetry={retry} />}
  >
    <WorkspaceInner
      subjectId={subjectId}
      kind={kind}
      visitId={visitId}
      roomUuid={roomUuid}
    />
  </AsyncBoundary>
);

interface SubjectWorkspaceProps {
  subjectId: string;
  kind: SubjectKind;
  /** The open patient's visit today, excluded from the rail's peers. */
  visitId: string | null;
  /** The room that visit is in — the source of the rail's peer group. */
  roomUuid: string | null;
}

const WorkspaceInner = ({
  subjectId,
  kind,
  visitId,
  roomUuid,
}: SubjectWorkspaceProps) => {
  const index = useSubjectIndex(subjectId);
  // The user's pick, or null for the default. See `resolveSelection`.
  const [picked, setPicked] = useState<PaneSelection | null>(null);

  const selection = resolveSelection({
    picked,
    views: index.views,
    tasks: index.tasks,
  });

  // A room indexes its own occupants; a patient indexes their room's.
  const peersRoomUuid = kind === "room" ? subjectId : roomUuid;

  return (
    <div className="flex min-h-0 flex-1">
      <SubjectRail
        index={index}
        subjectId={subjectId}
        kind={kind}
        roomUuid={peersRoomUuid}
        openVisitId={visitId}
        selected={selection}
        onSelect={setPicked}
      />
      <SubjectReader
        selection={selection}
        index={index}
        subjectId={subjectId}
        onClearSelection={() => setPicked(null)}
      />
    </div>
  );
};

/**
 * Blank rows and blocks written straight onto the folder's manila. Hand-rolled
 * rather than the kit `Skeleton`: manila needs an ink tint to show a blank at
 * all, and neither kit shape is one (`bar` is `bg-recess`, `sheet` is white
 * card stock with a border and a lift).
 */
const WorkspaceSkeleton = () => (
  <div className="flex min-h-0 flex-1">
    {/* Same width and rule as the real rail, so nothing jumps or flips. */}
    <div className="flex w-80 shrink-0 flex-col gap-2 border-r border-rule px-3 py-5">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="h-8 animate-pulse rounded-paper bg-ink/10" />
      ))}
    </div>
    <div className="flex min-h-0 flex-1 flex-col gap-4 p-7">
      <div className="h-7 w-1/3 animate-pulse rounded-paper bg-ink/10" />
      <div className="h-40 animate-pulse rounded-sheet bg-ink/10" />
    </div>
  </div>
);
